import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  ReactElement,
  ReactNode,
  useState,
} from 'react';

import { Box } from '@mui/material';
import clsx from 'clsx';

import { MarketingBannerCloseAnimation } from './MarketingBanner';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';

const classNamesFactory = classNamesFor('MarketingBannersStack');

export const marketingBannersStackClassNames = {
  root: classNamesFactory.root(),
};

const STACK_PEEK_PX = 10;
const STACK_SCALE_STEP = 0.02;
/** Trailing edges (peeks) visible behind the front card; deeper layers are hidden. */
export const MARKETING_BANNERS_MAX_PEEKS = 2;
/**
 * Atmospheric perspective: brightness of the 1st / 2nd visible back layer.
 * A filter — not a color — so it is identical in light and dark themes.
 */
const BACK_LAYER_BRIGHTNESS = [0.8, 0.64] as const;

type BannerStackChildProps = {
  onClose?: () => void;
  closeAnimation?: MarketingBannerCloseAnimation;
  sx?: SxProps;
};

const getBannerKey = (banner: ReactNode): string | number => {
  if (!isValidElement(banner) || banner.key == null) {
    throw new Error(
      'MarketingBannersStack requires a stable React key on every banner.'
    );
  }
  return banner.key;
};

export type MarketingBannersStackProps = {
  children: ReactNode;
  testId?: string;
  className?: string;
  sx?: SxProps;
};

export const MarketingBannersStack = ({
  children,
  testId = 'marketing-banners-stack',
  className,
  sx,
}: MarketingBannersStackProps) => {
  const [dismissedKeys, setDismissedKeys] = useState(
    () => new Set<string | number>()
  );

  // Prefer forEach over toArray so React does not invent positional keys
  // (toArray would mask missing stable keys).
  const visible: { banner: ReactNode; key: string | number }[] = [];
  const seenKeys = new Set<string | number>();
  Children.forEach(children, (banner) => {
    if (banner == null || typeof banner === 'boolean') {
      return;
    }
    if (isValidElement(banner) && banner.type === Fragment) {
      throw new Error(
        'MarketingBannersStack does not accept Fragments; pass MarketingBanner elements as direct children with stable keys.'
      );
    }
    const key = getBannerKey(banner);
    if (seenKeys.has(key)) {
      throw new Error(
        `MarketingBannersStack requires unique React keys; duplicate key: ${String(
          key
        )}`
      );
    }
    seenKeys.add(key);
    if (!dismissedKeys.has(key)) {
      visible.push({ banner, key });
    }
  });

  if (visible.length === 0) {
    return null;
  }

  const closeAnimation: MarketingBannerCloseAnimation =
    visible.length > 1 ? 'fade' : 'collapse';
  const peekCount = Math.min(visible.length - 1, MARKETING_BANNERS_MAX_PEEKS);

  return (
    <Box
      className={clsx(marketingBannersStackClassNames.root, className)}
      data-testid={testId}
      sx={mergeSx(
        {
          display: 'grid',
          paddingBottom: `${peekCount * STACK_PEEK_PX}px`,
          transition: 'padding-bottom 220ms cubic-bezier(0.2, 0, 0, 1)',
        },
        sx
      )}
    >
      {visible.map(({ banner, key }, index) => {
        const isTop = index === 0;
        const beyondPeeks = index > MARKETING_BANNERS_MAX_PEEKS;
        const depth = Math.min(index, MARKETING_BANNERS_MAX_PEEKS);
        const layered = isTop
          ? undefined
          : `translateY(${depth * STACK_PEEK_PX}px) scale(${
              1 - depth * STACK_SCALE_STEP
            })`;

        const content = isValidElement(banner)
          ? cloneElement(banner as ReactElement<BannerStackChildProps>, {
              closeAnimation,
              onClose: () => {
                (banner.props as BannerStackChildProps).onClose?.();
                setDismissedKeys((prev) => new Set(prev).add(key));
              },
              sx: mergeSx((banner.props as BannerStackChildProps).sx, {
                mt: 24,
              }),
            })
          : banner;

        return (
          <Box
            key={key}
            data-testid={`${testId}-layer`}
            // Covered layers must not receive pointer/keyboard focus
            {...(!isTop ? { inert: true } : {})}
            sx={{
              // Grid wrapper (stretched to the shared cell) makes every banner
              // fill the tallest card's height — peeks stay aligned regardless
              // of content length.
              display: 'grid',
              gridArea: '1 / 1',
              zIndex: visible.length - index,
              minWidth: 0,
              width: '100%',
              transform: layered,
              transformOrigin: 'top center',
              ...(beyondPeeks && { visibility: 'hidden' }),
              // filter rides a shorter spring than transform: brightness
              // arrives early (~85% in the first ~80ms) instead of trailing
              // with the slide.
              transition:
                'transform 450ms cubic-bezier(0.3, 0.9, 0.3, 1), filter 220ms cubic-bezier(0.3, 0.9, 0.3, 1)',
              ...(!isTop && {
                // Depth via atmospheric perspective — see BACK_LAYER_BRIGHTNESS.
                filter: `brightness(${
                  index <= BACK_LAYER_BRIGHTNESS.length
                    ? BACK_LAYER_BRIGHTNESS[index - 1]
                    : BACK_LAYER_BRIGHTNESS[BACK_LAYER_BRIGHTNESS.length - 1]
                })`,
                pointerEvents: 'none',
              }),
            }}
          >
            {content}
          </Box>
        );
      })}
    </Box>
  );
};
